/**
 * A static, human-maintained mirror of `docs/integrations-architecture.md` and `docs/integrations.md` —
 * NOT a live health check. None of the six real interfaces below exposes a "connection status" endpoint to
 * poll (they are Mock service implementations selected at DI-registration time, not a live network
 * connection with a health probe), so a live-status screen would either fake a check or add speculative
 * new backend surface for no real capability. See `docs/coming-later-feature-assessment.md`.
 */
export type IntegrationStatus = 'Sandbox / Mock-Only' | 'Not Configured';

export interface IntegrationRow {
  provider: string;
  purpose: string;
  status: IntegrationStatus;
  environment: string;
  owningModule: string | null;
  capabilities: string[];
  dataDirection: string;
}

export const INTEGRATION_STATUS: IntegrationRow[] = [
  {
    provider: 'Symmetry Tax Engine', purpose: 'Payroll tax calculation',
    status: 'Sandbox / Mock-Only', environment: 'Development / Staging',
    owningModule: 'Module 6 — EPN.PayrollCalculation.Api (ITaxCalculationProvider)',
    capabilities: ['Calculate employee/employer tax lines for a payroll calculation'],
    dataDirection: 'Outbound (calculation request) / Inbound (tax line results)',
  },
  {
    provider: 'KotaPay / NatPay (ACH — payroll)', purpose: 'Direct-deposit payment transmission',
    status: 'Sandbox / Mock-Only', environment: 'Development / Staging',
    owningModule: 'Module 8 — EPN.PaymentProcessing.Api (IAchProvider)',
    capabilities: ['Upload an ACH file for a payment batch', 'Check a submitted file\'s settlement status'],
    dataDirection: 'Outbound (file upload) / Inbound (status check)',
  },
  {
    provider: 'KotaPay / NatPay (ACH — tax remittance)', purpose: 'Tax-deposit payment transmission',
    status: 'Sandbox / Mock-Only', environment: 'Development / Staging',
    owningModule: 'Module 9 — EPN.TaxCompliance.Api (ITaxRemittanceProvider)',
    capabilities: ['Upload an ACH file for a tax deposit', 'Check a submitted file\'s settlement status'],
    dataDirection: 'Outbound (file upload) / Inbound (status check)',
  },
  {
    provider: 'Out-of-repo tax filing queue', purpose: 'Tax-filing submission (941/DE9/DE9C-style stub)',
    status: 'Sandbox / Mock-Only', environment: 'Development / Staging',
    owningModule: 'Module 9 — EPN.TaxCompliance.Api (ITaxFilingProvider)',
    capabilities: ['Submit a lightweight filing stub (no dollar amounts, no itemized form data)'],
    dataDirection: 'Outbound only (submission)',
  },
  {
    provider: 'QuickBooks Online / NolaPro', purpose: 'General ledger export',
    status: 'Sandbox / Mock-Only', environment: 'Development / Staging',
    owningModule: 'Module 11 — EPN.Accounting.Api (IGlExportProvider)',
    capabilities: ['Post a GL voucher for an exported payroll run'],
    dataDirection: 'Outbound only (voucher post)',
  },
  {
    provider: 'AWS S3', purpose: 'Employee document storage',
    status: 'Sandbox / Mock-Only', environment: 'Development / Staging',
    owningModule: 'Module 2 — EPN.Employees.Api (IDocumentStorageProvider, currently disk-backed)',
    capabilities: ['Store an uploaded document', 'Read a stored document back for download'],
    dataDirection: 'Bidirectional (upload / download)',
  },
  {
    provider: 'escoHR', purpose: 'Unconfirmed — no interface or consumer exists in this codebase',
    status: 'Not Configured', environment: '—', owningModule: null,
    capabilities: [], dataDirection: '—',
  },
  {
    provider: 'Reconzyle (the standalone product)', purpose: 'Unconfirmed — no interface or consumer exists in this codebase (not to be confused with Module 11\'s internal "Reconzyle" naming for its QuickBooks/NolaPro GL adapter)',
    status: 'Not Configured', environment: '—', owningModule: null,
    capabilities: [], dataDirection: '—',
  },
  {
    provider: 'Email (SMTP)', purpose: 'No current EPN feature sends email — no password reset or invitation-email flow exists yet',
    status: 'Not Configured', environment: '—', owningModule: null,
    capabilities: [], dataDirection: '—',
  },
];
